/**
 * Prüfbefund (PV-M04): Besichtigen und Erproben als Checkliste, die Punkte
 * aus der Planung, die Messwerte und das Ergebnis (Wunsch von Mathias, 11/2026).
 *
 * Vorher standen hier nur die Messwerte. Die Prüfpunkte kommen aus der Vorlage
 * in den Einstellungen und werden beim ersten Haken in den Befund kopiert.
 * Was die Planung ohnehin rechnet — Stringspannung, Systemspannung,
 * Spannungsfall —, wird angezeigt, nicht abgefragt.
 */

import { useRef } from 'react';

import {
  planungspunkte,
  pruefstand,
  punkteAusVorlage,
  widerspruchImErgebnis,
  type Pruefergebnis,
  type Pruefpunkt,
  type Pruefvorlage,
} from '@pv/geometrie';

import { neueId, speichere } from '../daten/db.ts';
import { tagVon } from '../datum.ts';
import type { Anlagendaten } from '../anlage.ts';
import type { Befundwerte, Pruefbefundzeile, Stringzeile } from '../typen.ts';
import { Zahlfeld } from './Zahlfeld.tsx';

interface Eigenschaften {
  projektId: string;
  auftragId: string | null;
  befund: Pruefbefundzeile | null;
  vorlage: Pruefvorlage;
  anlage: Anlagendaten;
  strings: Stringzeile[];
}

const ERGEBNISSE: Array<[Pruefergebnis, string]> = [
  ['io', 'i. O.'],
  ['mangel', 'Mangel'],
  ['entfaellt', 'entfällt'],
];

const ZEICHEN: Record<'io' | 'mangel' | 'offen', string> = { io: '✓', mangel: '✗', offen: '○' };

export function Pruefbefundpanel(e: Eigenschaften): React.JSX.Element {
  const { befund } = e;

  // Wer zwei Punkte schnell hintereinander abhakt, darf den ersten nicht
  // verlieren: Der zweite rechnet vom zuletzt angeforderten Stand, solange die
  // Datenbank den ersten noch nicht zurückgemeldet hat. Und solange es den
  // Befund nur als Anforderung gibt, wird kein zweiter angelegt.
  const angefordert = useRef<{ basis: Pruefbefundzeile | null; stand: Pruefbefundzeile } | null>(null);

  async function aendereBefund(
    aenderung: (vorher: Pruefbefundzeile | null) => Partial<Pruefbefundzeile>,
  ): Promise<void> {
    const offen = angefordert.current;
    const vorher = offen && offen.basis === befund ? offen.stand : befund;
    const felder = aenderung(vorher);
    if (vorher) {
      angefordert.current = { basis: befund, stand: { ...vorher, ...felder } };
      await speichere('pruefbefunde', vorher, felder as Record<string, unknown>);
      return;
    }
    const neu: Pruefbefundzeile = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      auftrag_id: e.auftragId,
      messwerte: {},
      pruefer: null,
      geprueft_am: null,
      bemerkungen: null,
      ...felder,
    };
    angefordert.current = { basis: befund, stand: neu };
    await speichere('pruefbefunde', neu, {
      projekt_id: neu.projekt_id,
      auftrag_id: neu.auftrag_id,
      messwerte: neu.messwerte,
      pruefer: neu.pruefer,
      geprueft_am: neu.geprueft_am,
      bemerkungen: neu.bemerkungen,
    });
  }

  const aendereMesswerte = (aenderung: (alt: Befundwerte) => Partial<Befundwerte>): void =>
    void aendereBefund((vorher) => {
      const alt = vorher?.messwerte ?? {};
      return { messwerte: { ...alt, ...aenderung(alt) } };
    });

  const punkte: Pruefpunkt[] = befund?.messwerte.pruefpunkte ?? punkteAusVorlage(e.vorlage);
  const ausVorlage = !befund?.messwerte.pruefpunkte;

  /** Einen Punkt ändern — die Liste kommt aus dem Befund oder beim ersten Mal aus der Vorlage. */
  const setzePunkt = (index: number, aenderung: Partial<Pruefpunkt>): void =>
    aendereMesswerte((alt) => {
      const liste = alt.pruefpunkte ?? punkteAusVorlage(e.vorlage);
      return { pruefpunkte: liste.map((p, i) => (i === index ? { ...p, ...aenderung } : p)) };
    });

  const ausPlanung = planungspunkte({
    befund: e.anlage.befund,
    wechselrichter: !!e.anlage.wechselrichter,
    systemspannungBekannt: e.anlage.systemspannungBekannt,
    spannungsfaelle: [...e.anlage.spannungsfaelle.values()].flat(),
  });
  const stand = pruefstand([...punkte, ...ausPlanung]);
  const widerspruch = widerspruchImErgebnis(befund?.messwerte.ergebnis, stand);
  const offeneStrings = e.strings.filter((s) => !s.geloescht_am);
  const pruefmittel = befund?.messwerte.pruefmittel?.[0] ?? {};

  // Abschnitte in der Reihenfolge der Liste, mit dem Index des Punkts.
  const abschnitte: Array<{ titel: string; punkte: Array<{ punkt: Pruefpunkt; index: number }> }> = [];
  punkte.forEach((punkt, index) => {
    const letzter = abschnitte[abschnitte.length - 1];
    if (letzter && letzter.titel === punkt.abschnitt) letzter.punkte.push({ punkt, index });
    else abschnitte.push({ titel: punkt.abschnitt, punkte: [{ punkt, index }] });
  });

  return (
    <div className="pruefbefund">
      <h2 style={{ marginTop: '1.5rem' }}>Prüfbefund</h2>
      <p className="klein gedaempft" style={{ margin: '0 0 0.6rem' }}>
        Besichtigen, Erproben, Messen — Punkt für Punkt. Die Anlagendaten sind vorbefüllt; das
        Programm ersetzt keinen Fachbefund.
      </p>

      <div className="knopfreihe">
        {(
          [
            ['erstpruefung', 'Erstprüfung'],
            ['wiederkehrend', 'Wiederkehrend'],
            ['ausserordentlich', 'Außerordentlich'],
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            aria-pressed={(befund?.messwerte.art ?? 'erstpruefung') === wert}
            onClick={() => aendereMesswerte(() => ({ art: wert }))}
          >
            {text}
          </button>
        ))}
      </div>

      <dl className="zahlenblock">
        <div>
          <dt>Geprüft</dt>
          <dd>
            {stand.gesamt - stand.offen} von {stand.gesamt}
          </dd>
        </div>
        <div>
          <dt>Mängel</dt>
          <dd style={stand.mangel > 0 ? { color: 'var(--fehler)' } : undefined}>{stand.mangel}</dd>
        </div>
      </dl>

      {/* --- Aus der Planung --------------------------------------------- */}
      <h3>Aus der Planung</h3>
      <p className="klein gedaempft" style={{ margin: '0 0 0.4rem' }}>
        Gerechnet, nicht abgehakt. Ändert sich die Planung, ändert sich das hier mit.
      </p>
      <ul className="pruefliste">
        {ausPlanung.map((p) => (
          <li key={p.text} className={`pruefpunkt pruefpunkt--${p.ergebnis}`}>
            <span className="pruefpunkt__zeichen" aria-hidden>
              {ZEICHEN[p.ergebnis]}
            </span>
            <span>
              {p.text}
              {p.grund && <span className="klein gedaempft"> — {p.grund}</span>}
            </span>
          </li>
        ))}
      </ul>

      {/* --- Besichtigen und Erproben ------------------------------------ */}
      {abschnitte.map((abschnitt) => (
        <section key={abschnitt.titel}>
          <h3>{abschnitt.titel}</h3>
          <ul className="pruefliste">
            {abschnitt.punkte.map(({ punkt, index }) => (
              <li
                key={index}
                className={`pruefpunkt pruefpunkt--${punkt.ergebnis ?? 'offen'}`}
                style={{ flexWrap: 'wrap' }}
              >
                <span className="pruefpunkt__text">{punkt.text}</span>
                <span className="pruefpunkt__wahl">
                  {ERGEBNISSE.map(([wert, text]) => (
                    <button
                      key={wert}
                      aria-pressed={punkt.ergebnis === wert}
                      onClick={() => setzePunkt(index, { ergebnis: punkt.ergebnis === wert ? null : wert })}
                    >
                      {text}
                    </button>
                  ))}
                </span>
                {punkt.ergebnis === 'mangel' && (
                  <input
                    key={`m-${index}-${punkt.bemerkung ?? ''}`}
                    className="pruefpunkt__bemerkung"
                    placeholder="Was ist der Mangel, was ist zu tun?"
                    defaultValue={punkt.bemerkung ?? ''}
                    onBlur={(ereignis) => {
                      const text = ereignis.target.value.trim() || null;
                      if (text !== (punkt.bemerkung ?? null)) setzePunkt(index, { bemerkung: text });
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {ausVorlage && punkte.length > 0 && (
        <p className="klein gedaempft">
          Die Punkte kommen aus der Vorlage in den Einstellungen („Errichter und Abwicklung“). Mit dem
          ersten Haken gehören sie zu diesem Befund.
        </p>
      )}
      {punkte.length === 0 && (
        <div className="hinweis">
          Keine Prüfpunkte hinterlegt. In den Einstellungen unter „Errichter und Abwicklung“ eintragen.
        </div>
      )}

      {/* --- Messen ------------------------------------------------------ */}
      <h3>Messen je String</h3>
      {offeneStrings.length === 0 ? (
        <div className="hinweis">Ohne Verstringung keine Messung je String.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th>String</th>
              <th>U_L in V</th>
              <th>I_K in A</th>
              <th>R_iso MΩ</th>
            </tr>
          </thead>
          <tbody>
            {offeneStrings.map((string) => {
              const messwert = befund?.messwerte.strings?.find((m) => m.string === string.name);
              const setze = (feld: string, wert: number | null): void =>
                aendereMesswerte((alt) => {
                  const alte = alt.strings ?? [];
                  const bisher = alte.find((m) => m.string === string.name) ?? { string: string.name };
                  return {
                    strings: [
                      ...alte.filter((m) => m.string !== string.name),
                      { ...bisher, [feld]: wert ?? undefined },
                    ],
                  };
                });
              return (
                <tr key={string.id}>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '0.7rem',
                        height: '0.7rem',
                        background: string.farbe,
                        marginRight: '0.35rem',
                      }}
                    />
                    {string.name}
                    <span className="gedaempft"> ({string.module.length})</span>
                  </td>
                  {(
                    [
                      ['leerlaufspannungV', messwert?.leerlaufspannungV],
                      ['kurzschlussstromA', messwert?.kurzschlussstromA],
                      ['isolationswiderstandMOhm', messwert?.isolationswiderstandMOhm],
                    ] as const
                  ).map(([feld, wert]) => (
                    <td key={feld}>
                      <Zahlfeld wert={wert} leerErlaubt onWert={(n) => setze(feld, n)} style={{ padding: '0.2rem' }} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Isolationsmessung — im Protokoll Abschnitt 3.2.1, bei der Erstpruefung
          ohne Strangdioden, ohne Ueberspannungsschutz, ohne Wechselrichter. */}
      <h3 style={{ marginTop: '1rem' }}>Isolationswiderstand Gleichstromseite</h3>
      <div className="feldraster">
        {(
          [
            ['pruefspannungV', 'Prüfspannung V'],
            ['plusPeMOhm', 'Plus/PE MΩ'],
            ['minusPeMOhm', 'Minus/PE MΩ'],
          ] as const
        ).map(([feld, text]) => (
          <label key={feld} className="klein" style={{ display: 'block' }}>
            {text}
            <Zahlfeld
              wert={befund?.messwerte.isolation?.[feld]}
              leerErlaubt
              onWert={(n) =>
                aendereMesswerte((alt) => ({
                  isolation: {
                    ...(alt.isolation ?? {}),
                    [feld]: n ?? undefined,
                  },
                }))
              }
            />
          </label>
        ))}
      </div>

      <h3 style={{ marginTop: '1rem' }}>Prüfmittel</h3>
      <div className="feldraster">
        {(
          [
            ['hersteller', 'Hersteller'],
            ['type', 'Typ'],
            ['seriennummer', 'Seriennummer'],
          ] as const
        ).map(([feld, text]) => (
          <label key={feld} className="klein" style={{ display: 'block' }}>
            {text}
            <input
              defaultValue={pruefmittel[feld] ?? ''}
              onBlur={(ereignis) => {
                const wert = ereignis.target.value.trim() || undefined;
                aendereMesswerte((alt) => {
                  const [erstes, ...rest] = alt.pruefmittel ?? [];
                  return { pruefmittel: [{ ...(erstes ?? {}), [feld]: wert }, ...rest] };
                });
              }}
            />
          </label>
        ))}
      </div>

      {/* --- Ergebnis ---------------------------------------------------- */}
      <h3 style={{ marginTop: '1rem' }}>Ergebnis</h3>
      <div className="knopfreihe">
        {(
          [
            ['in_ordnung', 'In Ordnung'],
            ['geringe_maengel', 'Geringe Mängel'],
            ['nicht_in_ordnung', 'Nicht in Ordnung'],
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            aria-pressed={befund?.messwerte.ergebnis === wert}
            onClick={() => aendereMesswerte(() => ({ ergebnis: wert }))}
          >
            {text}
          </button>
        ))}
      </div>
      {widerspruch && <div className="hinweis hinweis--warnung">{widerspruch}</div>}
      {befund?.messwerte.ergebnis === 'geringe_maengel' && (
        <label className="klein">
          Mängel beheben binnen Wochen
          <input
            type="text"
            inputMode="numeric"
            key={`frist-${befund.messwerte.maengelfristWochen ?? ''}`}
            defaultValue={befund.messwerte.maengelfristWochen ?? ''}
            onBlur={(ereignis) => {
              const roh = ereignis.target.value.trim();
              const wochen = Number(roh);
              aendereMesswerte(() => ({
                maengelfristWochen: roh === '' || !Number.isFinite(wochen) ? undefined : wochen,
              }));
            }}
            style={{ width: '6rem' }}
          />
        </label>
      )}

      <div className="feld" style={{ marginTop: '0.6rem' }}>
        <label htmlFor="befundbemerkung">Bemerkungen</label>
        <textarea
          id="befundbemerkung"
          rows={3}
          key={`bem-${befund?.id ?? 'neu'}`}
          defaultValue={befund?.bemerkungen ?? ''}
          onBlur={(ereignis) => {
            const text = ereignis.target.value.trim() || null;
            if (text !== (befund?.bemerkungen ?? null)) void aendereBefund(() => ({ bemerkungen: text }));
          }}
        />
      </div>

      <div className="feldraster" style={{ marginTop: '0.6rem' }}>
        <label className="klein" style={{ display: 'block' }}>
          Prüferin oder Prüfer
          <input
            defaultValue={befund?.pruefer ?? ''}
            onBlur={(ereignis) => void aendereBefund(() => ({ pruefer: ereignis.target.value || null }))}
          />
        </label>
        <label className="klein" style={{ display: 'block' }}>
          Geprüft am
          <input
            type="date"
            key={`gp-${tagVon(befund?.geprueft_am) ?? ''}`}
            defaultValue={tagVon(befund?.geprueft_am) ?? ''}
            onBlur={(ereignis) => {
              const wert = ereignis.target.value || null;
              if (wert !== tagVon(befund?.geprueft_am)) void aendereBefund(() => ({ geprueft_am: wert }));
            }}
          />
        </label>
      </div>
    </div>
  );
}
